import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { usePrimeHub } from "../../../lib/kitVariant";
import { useNavigate } from "react-router-dom";
import type {
  KernelEvent,
  ModeDefinition,
  RunBundle,
  RuntimeConfig,
  StageFrame,
} from "@picnic-field/local-host/protocol";
import type { PaletteSet } from "@picnic-field/renderer/appearance";
import { latestSnapshot } from "../../../lib/api/fieldApi";
import type { Snapshot } from "../../../lib/api/fieldApi";
import { useFieldApi } from "../../../hooks/useFieldApi";
import { useCourseApi } from "../../../lib/api/courseApi";
import { useSocialApi } from "../../../lib/api/socialApi";
import { useMissionApi } from "../../../lib/api/missionApi";
import type { Mission } from "../../../lib/api/missionApi";
import { toApiError } from "../../../lib/http/errors";
import type { ErrorCode } from "../../../lib/http/errors";
import { Button, ButtonLink } from "../../../components/ui/Button";
import { FeBanner } from "../../../components/FeBanner";
import { PicoBubble } from "../../../components/PicoBubble";
import { ScreenRoot } from "../../../components/Shell";
import { useTopBar } from "../../../components/TopBar";
import { useDensity } from "../../../store/density";
import { useI18n } from "../../../i18n";
import { useHubView } from "../../../hooks/useHubView";
import { HubHud } from "../../../components/HubHud";
import {
  bandAfterPoll,
  bandAfterSave,
  bandAtFinish,
  CourseScene,
  flushOutbox,
  formatClock,
  MissionBand,
  missionPreset,
  POLL_MAX,
  POLL_MS,
  saveRun,
  useCourseRun,
} from "../../course";
import type { CourseViewLike, MissionRunView } from "../../course";
import {
  exciteOf,
  goalFill,
  goalLabel,
  goalValue,
  lessonKey,
  pickStage,
  readStageDecor,
  readStageModels,
  stageModelUrlFor,
  STAGE_MODE_ID,
  stageChange,
  stagePresets,
} from "../lib/stage";
import type { StageModelEntry } from "../lib/stage";
import { stageSound } from "../lib/sound";
import { HandPanel } from "./HandPanel";
import {
  StageGoalList,
  StagePico,
  StageResultHead,
  StageSoundButton,
  StageStars,
} from "./StageHud";
import "../../../styles/course.css";
import "../../../styles/stage.css";

/**
 * FS-SB 기구 무대(machine-stage · PICNIC-SB-FIELD-001 §6.1 · 2026-10-06) — 정적 기계(놀이기구 · 오토마타)를 받침대에 묶고
 * 아이 프로그램을 돌려 관절로 별 3단계를 잰다. 고르기(이야기 · 별 미션) → 놀이(HUD · 손님 반응) → 결과(별 · 미션 띠 · 다시 보기).
 * 판정은 커널(StageMeter) — 화면은 보여 주기만 한다. 순위 · 비교 없음(DR-01).
 */
type Screen = "select" | "play" | "result";

interface Data {
  config: RuntimeConfig;
  mode: ModeDefinition;
  snapshot: Snapshot | null;
  bundle: RunBundle | null;
  palette: PaletteSet;
  bundleError: string | null;
  /** 운영 콘솔이 배포한 무대 꾸밈(프리셋 키 → stage_scene payload) — 못 받으면 비어 기본 장면 */
  decor: ReadonlyMap<string, unknown>;
  /** 운영자가 올린 3D 모델(카탈로그 stage_model) — 꾸밈 models 가 키로 가리킨다 */
  models: ReadonlyMap<string, StageModelEntry>;
}

interface ResultState {
  completed: boolean;
  timeMs: number;
  stars: number;
  bonus: boolean;
  mission: MissionRunView | null;
  replayId: string | null;
  attemptId: string | null;
}

/** 카메라 — 기계 앞(+z)에서 살짝 위 · 옆 */
const FOLLOW = { back: -0.95, up: 0.4, side: 0.18, lookAhead: 0, stiffness: 3 };

export function StageScreen() {
  const { t, locale } = useI18n();
  const { density } = useDensity();
  const se = density === "se";
  const navigate = useNavigate();
  const fieldApi = useFieldApi();
  const courseApi = useCourseApi();
  const social = useSocialApi();
  const missionApi = useMissionApi();

  const [data, setData] = useState<Data | null>(null);
  const prime = usePrimeHub(data?.snapshot?.glbVersion);   // 스파이크 프라임 판이면 허브 이름도 그 교구로
  const [loadError, setLoadError] = useState<ErrorCode | null>(null);
  const [loadSeq, setLoadSeq] = useState(0);
  const [mission, setMission] = useState<Mission | null>(null);
  const [missionSeq, bumpMission] = useReducer((x: number) => x + 1, 0);
  const [presetKey, setPresetKey] = useState<string | null>(null);
  const chosen = useRef(false);
  const [screen, setScreen] = useState<Screen>("select");
  const [stage, setStage] = useState<StageFrame | null>(null);
  const [result, setResult] = useState<ResultState | null>(null);
  const [muted, setMuted] = useState(stageSound.muted);
  const [picoLine, setPicoLine] = useState<string | null>(null);
  const viewRef = useRef<CourseViewLike | null>(null);
  const lastStage = useRef<StageFrame | null>(null);
  const stillSince = useRef<number | null>(null);
  const saveSeq = useRef(0);

  // ── 데이터: 런타임 설정 · 무대 모드 · 내 스냅샷 · run-bundle · 팔레트 ──
  useEffect(() => {
    if (!fieldApi || !courseApi) return;
    let cancelled = false;
    setLoadError(null);
    (async () => {
      const [config, mode, list, decor, models] = await Promise.all([
        courseApi.runtimeConfig(),
        courseApi.mode(STAGE_MODE_ID),
        fieldApi.mySnapshots(),
        courseApi.catalog("stage_scene").then(readStageDecor, () => new Map<string, unknown>()),
        courseApi
          .catalog("stage_model")
          .then(readStageModels, () => new Map<string, StageModelEntry>()),
      ]);
      const snapshot = latestSnapshot(list);
      const palette = await fieldApi.palettes([snapshot?.appearance?.palette]);
      let bundle: RunBundle | null = null;
      let bundleError: string | null = null;
      if (snapshot)
        try {
          bundle = await courseApi.runBundle(snapshot.snapshotId);
        } catch (e) {
          const err = toApiError(e);
          if (err.status === 0 || err.status >= 500) throw e;
          bundleError = err.code;
        }
      if (cancelled) return;
      setData({
        config,
        mode: mode.definition,
        snapshot,
        bundle,
        palette,
        bundleError,
        decor,
        models,
      });
    })().catch((e) => {
      if (!cancelled) setLoadError(toApiError(e).code);
    });
    return () => {
      cancelled = true;
    };
  }, [fieldApi, courseApi, loadSeq]);

  // ── 미션(없으면 null) ──
  useEffect(() => {
    if (!missionApi) return;
    let cancelled = false;
    void missionApi.mission().then((m) => {
      if (!cancelled) setMission(m);
    });
    return () => {
      cancelled = true;
    };
  }, [missionApi, missionSeq]);

  const presets = stagePresets(data?.mode ?? null);
  // 처음 무대 — 미션 > 내 기계 모형 > 첫 무대(학생이 고르면 그대로)
  useEffect(() => {
    if (!data || chosen.current) return;
    const k = pickStage(presets, missionPreset(mission, STAGE_MODE_ID), data.snapshot?.modelSlug);
    if (k) setPresetKey(k);
    // presets 는 data 에서 나온다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, mission]);
  const preset = presets.find((p) => p.key === presetKey) ?? null;
  // 올린 3D 모델 — 키 → blob URL(Bearer 로 받아 렌더러에 · 모델이 없으면 넘기지 않는다)
  const stageModelUrl = useMemo(
    () =>
      courseApi && data?.models.size
        ? stageModelUrlFor(data.models, (sha) => courseApi.stageModel(sha))
        : null,
    [courseApi, data?.models],
  );
  const lk = presetKey ? lessonKey(presetKey) : null;
  const lessonText = (k: string) => (lk ? t(`stage.lessons.${lk}.${k}`) : "");
  /** 별 목표 문구 — 운영자가 고친 문구(모드 정의 label)가 있으면 그것 */
  const goalText = (i: number) => goalLabel(preset, i - 1, locale) ?? lessonText(`g${i}`);

  useTopBar({
    icon: "🎡",
    title: t("stage.title"),
    chips: preset
      ? [{ label: `${preset.stage!.lesson} · ${lessonText("title")}`, testId: "stage-chip" }]
      : [],
  });

  // ── 커널 호스트 ──
  const { hub: hubView, now: hubNow, onEvent: onHubOut } = useHubView();
  const onEvent = useCallback((e: KernelEvent) => onHubOut(e), [onHubOut]);
  const run = useCourseRun({
    bundle: data?.bundle ?? null,
    mode: data?.mode ?? null,
    config: data?.config ?? null,
    onEvent,
  });
  const hostReady = run.status !== "creating" && run.status !== "error";
  const canRun = !!data?.bundle?.program;
  useEffect(() => {
    if (!data?.bundle || !presetKey || !hostReady || screen !== "select") return;
    lastStage.current = null;
    setStage(null);
    void run.prepare(presetKey, []);
    // run.prepare 는 bundle · mode · config 가 같으면 같은 함수
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, hostReady, presetKey, screen === "select"]);

  // ── 무대 진행(프레임) → HUD · 손님 반응 · 소리 · 피코 ──
  useEffect(
    () =>
      run.onFrame((f) => {
        const s = f.stage;
        if (!s) return;
        const prev = lastStage.current;
        const ch = stageChange(prev, s);
        const view = viewRef.current?.stage;
        view?.setExcite(exciteOf(s));
        if (ch.star) {
          view?.react(ch.star >= 3 ? "cheer" : "star");
          if (ch.star >= 3) stageSound.fanfare();
          else stageSound.star();
          setPicoLine(t(ch.star >= 3 ? "stage.pico.allStars" : "stage.pico.star"));
        }
        if (ch.bonus) {
          view?.react("bonus");
          stageSound.bonus();
          setPicoLine(t("stage.pico.bonus"));
        }
        if (ch.note) stageSound.note(ch.note);
        // 가만히 오래(5초) — 모터를 확인하라는 피코 한마디(한 번)
        const moving = Math.abs(s.main?.rate ?? 0) > 1 || s.notes.length > 0;
        if (moving) stillSince.current = null;
        else if (stillSince.current === null) stillSince.current = f.timeMs;
        else if (f.timeMs - stillSince.current > 5000 && s.stars === 0 && !prev?.main?.rate) {
          stillSince.current = Number.POSITIVE_INFINITY;
          setPicoLine(t("stage.pico.still"));
        }
        lastStage.current = s;
        // 표시가 바뀔 때만 React 상태
        const key = `${s.stars}|${s.bonus?.done}|${s.goals.map((g) => goalValue(g)).join(",")}|${s.notes.length}`;
        const pk = prev
          ? `${prev.stars}|${prev.bonus?.done}|${prev.goals.map((g) => goalValue(g)).join(",")}|${prev.notes.length}`
          : "";
        if (key !== pk) setStage(s);
      }),
    [run, t],
  );

  // 판 끝 신호가 마지막 프레임(별 셋을 켠 틱)보다 먼저 올 수 있다 — 결과의 별 · 보너스는 늦게 온 프레임까지 따라간다(2026-10-06 운영: 장면 ★3 · 결과 ★2)
  useEffect(() => {
    if (!stage) return;
    setResult((r) =>
      r && (stage.stars > r.stars || (!!stage.bonus?.done && !r.bonus))
        ? { ...r, stars: Math.max(r.stars, stage.stars), bonus: r.bonus || !!stage.bonus?.done }
        : r,
    );
  }, [stage]);

  // 피코 한마디는 잠깐
  useEffect(() => {
    if (!picoLine) return;
    const h = setTimeout(() => setPicoLine(null), 3500);
    return () => clearTimeout(h);
  }, [picoLine]);

  // ── 판 끝 → 결과 · 리플레이 저장 · 미션 확인 ──
  useEffect(() => {
    if (run.run.phase !== "over" || !run.run.result || screen !== "play" || !data || !presetKey)
      return;
    const r = run.run.result;
    const s = lastStage.current;
    const stars = s?.stars ?? 0;
    if (!r.completed) {
      stageSound.oops();
      viewRef.current?.stage?.react("oops");
    }
    const seq = ++saveSeq.current;
    setResult({
      completed: r.completed,
      timeMs: r.timeMs,
      stars,
      bonus: !!s?.bonus?.done,
      mission: bandAtFinish(mission, STAGE_MODE_ID, presetKey, r.completed, r.timeMs),
      replayId: null,
      attemptId: null,
    });
    setScreen("result");
    void (async () => {
      const exp = await run.exportReplay();
      const saved = social
        ? await saveRun(
            social,
            exp,
            {
              snapshotId: data.snapshot?.snapshotId ?? "",
              modeId: STAGE_MODE_ID,
              presetKey,
              outcome: r.completed ? "complete" : "incomplete",
              durationMs: Math.round(r.timeMs),
              tries: r.attempts,
              failPattern: null,
            },
            data.bundle,
          )
        : ({ state: "failed", replayId: null, pendingId: null } as const);
      if (seq !== saveSeq.current) return;
      setResult((v) =>
        v
          ? {
              ...v,
              replayId: saved.replayId,
              mission: v.mission ? bandAfterSave(v.mission, saved) : v.mission,
            }
          : v,
      );
    })();
  }, [run, screen, data, presetKey, social, mission]);

  // 미션 확인 조회(서버 재생 검증 — 코스와 같다)
  const pollAttempt =
    result?.mission?.state === "pending" && !result.mission.slow
      ? (result.mission.attemptId ?? null)
      : null;
  useEffect(() => {
    if (!pollAttempt || !missionApi) return;
    let n = 0;
    let stopped = false;
    const h = setInterval(() => {
      n += 1;
      if (n > POLL_MAX) {
        clearInterval(h);
        setResult((v) => (v?.mission ? { ...v, mission: { ...v.mission, slow: true } } : v));
        return;
      }
      missionApi.verification(pollAttempt).then(
        (r) => {
          if (stopped) return;
          setResult((v) =>
            v?.mission && v.mission.attemptId === pollAttempt
              ? { ...v, mission: bandAfterPoll(v.mission, r) }
              : v,
          );
          if (r.status !== "pending") bumpMission();
        },
        () => undefined,
      );
    }, POLL_MS);
    return () => {
      stopped = true;
      clearInterval(h);
    };
  }, [pollAttempt, missionApi]);

  useEffect(() => {
    if (social) void flushOutbox(social);
  }, [social]);

  const start = useCallback(() => {
    if (run.status !== "ready" || !canRun) return;
    stageSound.unlock();
    stillSince.current = null;
    setResult(null);
    setPicoLine(t("stage.pico.start"));
    run.start();
    setScreen("play");
  }, [run, canRun, t]);

  const again = useCallback(async () => {
    if (!presetKey) return;
    lastStage.current = null;
    setStage(null);
    await run.prepare(presetKey, []);
    setResult(null);
    stillSince.current = null;
    run.start();
    setScreen("play");
  }, [run, presetKey]);

  // ── 그리기 ──
  if (loadError)
    return (
      <ScreenRoot id="FS-SB" title={t("stage.title")} hideTitle>
        <FeBanner
          code={loadError}
          audience="student"
          actions={[
            { label: t("common.retry"), icon: "⟲", onClick: () => setLoadSeq((n) => n + 1) },
          ]}
        />
      </ScreenRoot>
    );
  if (!data)
    return (
      <ScreenRoot id="FS-SB" title={t("stage.title")} hideTitle>
        <div className="pf-pane" aria-busy="true">
          <PicoBubble text={t("stage.loading")} busy />
        </div>
      </ScreenRoot>
    );
  if (!data.snapshot || !data.bundle)
    return (
      <ScreenRoot id="FS-SB" title={t("stage.title")} hideTitle>
        <div className="pf-pane pf-pane--hero" data-testid="stage-nomachine">
          <PicoBubble text={t("stage.noMachine")} live={false} />
          <ButtonLink to="/upload" variant="accent" icon="⬆">
            {t("student.home.upload")}
          </ButtonLink>
        </div>
      </ScreenRoot>
    );

  const screenTitle = t(`stage.screen.${screen}`);
  const goals = stage?.goals ?? [];
  const stars = stage?.stars ?? 0;
  const otherModel =
    preset?.stage && data.snapshot.modelSlug && preset.stage.model !== data.snapshot.modelSlug;

  return (
    <ScreenRoot
      id="FS-SB"
      title={screenTitle}
      hideTitle
      fill
      focusKey={screen}
      className={`pf-course pf-stage pf-course--${density} pf-stage--${screen}`}
    >
      <div className="pf-course__main">
        <div className="pf-course__left">
          <CourseScene
            run={run}
            load={run.load}
            bundle={data.bundle}
            palette={data.palette}
            ghosts={[]}
            onView={(v) => (viewRef.current = v)}
            label={t("stage.title")}
            follow={FOLLOW}
            orbit
            {...(preset?.stage
              ? {
                  stageScene: preset.stage.scene,
                  stageThemeData: data.decor.get(preset.key),
                  stageModelUrl: stageModelUrl ?? undefined,
                }
              : {})}
          >
            <StageStars stars={stars} />
            {screen === "play" && (
              <>
                <div className="pf-hud pf-hud--timer pf-stage__timer" data-testid="stage-timer">
                  {formatClock(run.hud.clockS, !se)}
                </div>
                <StageGoalList
                  goals={goals.map((g, i) => ({
                    text: goalText(i + 1),
                    done: g.done,
                    fill: goalFill(g),
                    num: `${t("stage.hud.goal", { value: goalValue(g), target: g.target })} ${t(`stage.hud.${g.measure}`)}`,
                  }))}
                  bonusDone={!!stage?.bonus?.done}
                />
                {stage && stage.notes.length > 0 && (
                  <div className="pf-hud pf-stage__notes" data-testid="stage-notes">
                    🎵 {stage.notes.slice(-8).join(" ")}
                  </div>
                )}
                <div className="pf-hud-stack pf-stage__hub">
                  <HubHud hub={hubView} now={hubNow} prime={prime} />
                </div>
                <StagePico line={picoLine} />
                {preset?.stage?.inputs && preset.stage.inputs.length > 0 && (
                  <HandPanel
                    inputs={preset.stage.inputs}
                    disabled={run.run.phase !== "driving"}
                    onHand={run.hand}
                    onButtons={run.input}
                  />
                )}
                <div className="pf-course__controls" role="group">
                  <Button
                    variant="ghost"
                    icon="⟲"
                    onClick={() => run.reset()}
                    data-testid="stage-reset"
                  >
                    {t("stage.reset")}
                  </Button>
                  <Button
                    variant="ghost"
                    icon="✋"
                    onClick={() => run.giveUp()}
                    data-testid="stage-stop"
                  >
                    {t("stage.stop")}
                  </Button>
                </div>
              </>
            )}
            <StageSoundButton
              muted={muted}
              onToggle={() => {
                stageSound.setMuted(!muted);
                setMuted(!muted);
              }}
            />
          </CourseScene>
        </div>
        {screen === "select" && (
          <aside className="pf-pane pf-stage__side" data-testid="stage-select">
            <label className="pf-stage__pick">
              <span>{t("stage.pick")}</span>
              <select
                value={presetKey ?? ""}
                data-testid="stage-pick"
                onChange={(e) => {
                  chosen.current = true;
                  setPresetKey(e.target.value);
                }}
              >
                {presets.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.stage!.lesson} · {t(`stage.lessons.${lessonKey(p.key)}.title`)}
                  </option>
                ))}
              </select>
            </label>
            {preset && (
              <>
                <h2 className="pf-stage__title">{lessonText("title")}</h2>
                <PicoBubble text={lessonText("story")} live={false} />
                <h3 className="pf-stage__subtitle">{t("stage.goals")}</h3>
                <ol className="pf-stage__goal-list">
                  {[1, 2, 3].map((i) => (
                    <li key={i}>
                      <span aria-hidden="true">{"⭐".repeat(i)} </span>
                      {goalText(i)}
                    </li>
                  ))}
                </ol>
                <p className="pf-muted">🎯 {t("stage.missionStar")}</p>
                <p className="pf-muted">🎁 {t("stage.bonusHidden")}</p>
                {otherModel && (
                  <p className="pf-banner pf-banner--info" data-testid="stage-other-model">
                    {t("stage.otherModel", { model: preset.stage!.model })}
                  </p>
                )}
              </>
            )}
            {!canRun && (
              <p className="pf-banner pf-banner--warn" data-testid="stage-noprogram">
                {t(prime ? "stage.noProgramPrime" : "stage.noProgram")}
              </p>
            )}
            {data.bundleError && (
              <FeBanner code={data.bundleError as ErrorCode} audience="student" />
            )}
            {run.status === "error" && (
              <FeBanner
                code="NET-SERVER"
                audience="student"
                actions={[
                  { label: t("common.retry"), icon: "⟲", onClick: () => window.location.reload() },
                ]}
              />
            )}
            <Button
              variant="accent"
              icon="▶"
              onClick={start}
              disabled={run.status !== "ready" || !canRun}
              data-testid="stage-start"
            >
              {t("stage.start")}
            </Button>
          </aside>
        )}
        {screen === "result" && result && (
          <aside
            className="pf-pane pf-stage__side"
            data-testid="stage-result"
            data-stars={result.stars}
          >
            <StageResultHead stars={result.stars} bonus={result.bonus} />
            {result.completed && (
              <p className="pf-muted">
                {t("stage.result.time", { time: formatClock(result.timeMs / 1000, !se) })}
              </p>
            )}
            {result.mission && <MissionBand view={result.mission} />}
            <div className="pf-row pf-row--wrap">
              <Button
                variant="accent"
                icon="⟲"
                onClick={() => void again()}
                data-testid="stage-again"
              >
                {t("stage.again")}
              </Button>
              {result.replayId && (
                <Button
                  variant="ghost"
                  icon="🎬"
                  onClick={() => navigate(`/replay/${encodeURIComponent(result.replayId!)}`)}
                  data-testid="stage-watch"
                >
                  {t("stage.result.watch")}
                </Button>
              )}
              <Button
                variant="ghost"
                icon="🎡"
                onClick={() => {
                  setResult(null);
                  setScreen("select");
                }}
                data-testid="stage-back"
              >
                {t("stage.pick")}
              </Button>
            </div>
          </aside>
        )}
      </div>
    </ScreenRoot>
  );
}
